import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import routeService from '../../services/RouteService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatLocationDropdownLabel, sortLocationsByNumber } from '../../utils/locationDropdownLabel.js';
import { Route as RouteIcon, Plus, Eye, X, MapPin, Flag, FileSpreadsheet } from 'lucide-react';
import DispatchExcelModal from './DispatchExcelModal.js';
import { useAuth } from '../../context/AuthContext.js';
import { canCreateMlSheet } from '../../utils/networkMode.js';
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';

const PATH_PREVIEW_MAX = 48;

const hubLabel = (hub) => {
  if (!hub?.location) return null;
  return hub.location_code ? `${hub.location} (${hub.location_code})` : hub.location;
};

const joinLocations = (hubs) =>
  (hubs || []).map((hub) => hub.location).filter(Boolean).join(', ');

const formatPath = (route) => {
  const chunks = [];
  const sources = joinLocations(
    route.source_locations?.length ? route.source_locations : (route.from_location ? [route.from_location] : [])
  );
  const targets = joinLocations(
    route.target_locations?.length ? route.target_locations : (route.to_location ? [route.to_location] : [])
  );
  if (sources) chunks.push(sources);
  (route.via_locations || []).forEach((hub) => {
    if (hub.location) chunks.push(hub.location);
  });
  if (targets) chunks.push(targets);
  return chunks.join(' → ') || '—';
};

/** Ordered stops for visual track: From → Via… → To */
const getRouteStops = (route) => {
  if (!route) return [];
  const stops = [];
  const sources = route.source_locations?.length
    ? route.source_locations
    : (route.from_location ? [route.from_location] : []);
  const targets = route.target_locations?.length
    ? route.target_locations
    : (route.to_location ? [route.to_location] : []);
  sources.forEach((hub) => {
    if (hub?.location) {
      stops.push({ name: hubLabel(hub), role: 'from', roleLabel: 'Source' });
    }
  });
  (route.via_locations || []).forEach((h, i) => {
    if (h.location) {
      stops.push({
        name: hubLabel(h),
        role: 'via',
        roleLabel: `Via ${i + 1}`,
      });
    }
  });
  targets.forEach((hub) => {
    if (hub?.location) {
      stops.push({ name: hubLabel(hub), role: 'to', roleLabel: 'Target' });
    }
  });
  return stops;
};

/** Preview + orange eye → visual route track modal */
function RoutePathCell({ path, onViewFull, singleLine = false }) {
  const text = path?.trim() || '';
  if (!text || text === '—') {
    return <span className="text-sm text-gray-500">—</span>;
  }

  const isLong = text.length > PATH_PREVIEW_MAX;
  const preview = isLong ? `${text.slice(0, PATH_PREVIEW_MAX).trimEnd()}…` : text;

  return (
    <div
      className={`flex min-w-0 gap-1.5 ${
        singleLine ? 'items-center w-full' : 'items-start max-w-xs lg:max-w-sm'
      }`}
    >
      <span
        className={`flex-1 min-w-0 ${
          singleLine
            ? 'truncate text-xs sm:text-sm text-gray-900'
            : 'text-sm text-gray-600 leading-snug line-clamp-2 break-words'
        }`}
      >
        {preview}
      </span>
      <button
        type="button"
        onClick={onViewFull}
        className="shrink-0 p-1 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors"
        title="View route track"
        aria-label="View route track"
      >
        <Eye className="w-4 h-4" />
      </button>
    </div>
  );
}

function crossingLocationName(point) {
  return hubLabel(point?.location) || point?.location?.location || 'Crossing location';
}

function CrossingLocationsModal({ route, onClose }) {
  const points = route?.crossing_points || [];
  const title = route?.name || route?.route_id || 'Route';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-3 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="route-crossing-modal-title"
      >
        <div className="flex items-center justify-between gap-2 px-3.5 sm:px-4 pt-3 pb-2.5 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h3 id="route-crossing-modal-title" className="text-sm sm:text-base font-semibold text-gray-800 truncate">
              Crossing Locations
            </h3>
            <p className="text-[11px] text-gray-500 truncate">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div className="px-3.5 sm:px-4 py-3 overflow-y-auto flex-1 min-h-0 space-y-3">
          {points.length === 0 ? (
            <p className="text-sm text-gray-500">No crossing locations</p>
          ) : (
            points.map((point) => {
              const branches = point.branches || [];
              return (
                <div key={point.location_hub_id} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-3 py-2 bg-orange-50 border-b border-orange-100">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-orange-700">Crossing location</p>
                    <p className="text-sm font-semibold text-gray-900 break-words">{crossingLocationName(point)}</p>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                      Branches
                    </p>
                    {branches.length === 0 ? (
                      <p className="text-sm text-gray-500">No branches selected</p>
                    ) : (
                      <ul className="space-y-1">
                        {branches.map((branch) => (
                          <li
                            key={branch.id}
                            className="text-sm text-gray-800 bg-gray-50 rounded-lg px-2.5 py-1.5 border border-gray-100 break-words"
                          >
                            {hubLabel(branch) || branch.location}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="px-3.5 sm:px-4 py-2.5 border-t border-gray-100 flex justify-end shrink-0">
          <Button variant="secondary" type="button" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function CrossingCell({ route, onOpen }) {
  const points = route?.crossing_points || [];
  if (!points.length) {
    return <span className="text-sm text-gray-500">—</span>;
  }
  const label = points.map(crossingLocationName).join(', ');
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left text-sm font-medium text-secondary hover:underline break-words max-w-[180px]"
      title="View crossing locations"
    >
      {label}
    </button>
  );
}

function RouteTrackModal({ route, onClose }) {
  const stops = getRouteStops(route);
  const title = route?.name || route?.route_id || 'Route Path';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-3 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="route-path-modal-title"
      >
        <div className="flex items-center justify-between gap-2 px-3.5 sm:px-4 pt-3 pb-2.5 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h3 id="route-path-modal-title" className="text-sm sm:text-base font-semibold text-gray-800 truncate">
              Route Track
            </h3>
            <p className="text-[11px] text-gray-500 truncate">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div className="px-3.5 sm:px-4 py-3 overflow-y-auto flex-1 min-h-0">
          {stops.length === 0 ? (
            <p className="text-sm text-gray-500">No path available</p>
          ) : (
            <div className="relative">
              {stops.map((stop, index) => {
                const isFirst = index === 0;
                const isLast = index === stops.length - 1;
                const tone = isFirst
                  ? { dot: 'bg-sky-500', ring: 'ring-sky-100', badge: 'bg-sky-50 text-sky-700 border-sky-100', line: 'bg-sky-200' }
                  : isLast
                    ? { dot: 'bg-emerald-500', ring: 'ring-emerald-100', badge: 'bg-emerald-50 text-emerald-700 border-emerald-100', line: 'bg-emerald-200' }
                    : { dot: 'bg-orange-400', ring: 'ring-orange-100', badge: 'bg-orange-50 text-orange-700 border-orange-100', line: 'bg-orange-200' };

                return (
                  <div key={`${stop.role}-${index}`} className="flex gap-2.5 min-w-0">
                    <div className="flex flex-col items-center shrink-0 w-5">
                      <div
                        className={`w-5 h-5 rounded-full ${tone.dot} ring-2 ${tone.ring} flex items-center justify-center text-white shrink-0`}
                      >
                        {isLast ? (
                          <Flag className="w-2.5 h-2.5" />
                        ) : (
                          <MapPin className="w-2.5 h-2.5" />
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 flex-1 min-h-[10px] my-0.5 rounded-full ${tone.line}`} />
                      )}
                    </div>

                    <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-2'}`}>
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span
                          className={`inline-flex items-center px-1.5 py-px rounded text-[9px] font-semibold border ${tone.badge} shrink-0`}
                        >
                          {stop.roleLabel}
                        </span>
                        <span className="text-[10px] text-gray-400 shrink-0">
                          {index + 1}/{stops.length}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm font-semibold text-gray-900 break-words leading-snug mt-0.5">
                        {stop.name}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-3.5 sm:px-4 py-2.5 border-t border-gray-100 flex justify-end shrink-0">
          <Button variant="secondary" type="button" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

const RouteList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromHubId, setFromHubId] = useState('');
  const [toHubId, setToHubId] = useState('');
  const [crossingHubId, setCrossingHubId] = useState('');
  const [viaHubId, setViaHubId] = useState('');
  const [hubs, setHubs] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [viewPathRoute, setViewPathRoute] = useState(null);
  const [viewCrossingRoute, setViewCrossingRoute] = useState(null);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const dropdownRef = useRef(null);
  const perPage = 10;

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (fromHubId) filters.from_location_hub_id = fromHubId;
      if (toHubId) filters.to_location_hub_id = toHubId;
      if (crossingHubId) filters.crossing_location_hub_id = crossingHubId;
      if (viaHubId) filters.via_location_hub_id = viaHubId;
      const response = await routeService.getAll(currentPage, perPage, filters);
      const rows = Array.isArray(response?.data) ? response.data : [];
      if (response?.success === false) {
        throw new Error(response?.error || 'Failed to load routes');
      }
      setRoutes((prev) => mergeLoadedPage(currentPage, rows, prev));
      setTotalPages(response.pagination?.pages || 1);
      setTotalCount(response.pagination?.total ?? response.total ?? 0);
    } catch (err) {
      if (currentPage <= 1) {
        Alert.error(err.response?.data?.error || err.message || 'Failed to load routes');
      } else {
        console.error('Failed to load more routes:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [search, fromHubId, toHubId, crossingHubId, viaHubId, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchRoutes,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, fromHubId, toHubId, crossingHubId, viaHubId],
    debounceWhen: search,
    debounceMs: 0,
  });

  useEffect(() => {
    locationHubService.getAllHubs(1, 1000, { all: 'true' })
      .then((res) => {
        const list = res.data?.hubs || res.data?.data || res.data || [];
        setHubs(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  const orderedHubs = useMemo(() => sortLocationsByNumber(hubs), [hubs]);

  const handleDelete = async (routeId, routeName) => {
    setActiveDropdown(null);
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${routeName}"? ML sheets will fall back to unsorted order if no route matches.`,
      'Delete Route'
    );
    if (!confirmed) return;

    try {
      await routeService.delete(routeId);
      Alert.success('Route deleted successfully!');
      if (routes.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchRoutes();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete route');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFromHubId('');
    setToHubId('');
    setCrossingHubId('');
    setViaHubId('');
    setCurrentPage(1);
  };

  const locationFilter = (label, value, onChange) => (
    <div>
      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 bg-white"
      >
        <option value="">All locations</option>
        {orderedHubs.map((hub) => (
          <option key={hub.id} value={hub.id}>
            {formatLocationDropdownLabel(hub)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <ListPageHeader title="Routes" icon={RouteIcon} className="mb-4 sm:mb-4">
          {canCreateMlSheet(user) && (
          <Button mobileIconOnly variant="secondary" icon={FileSpreadsheet} onClick={() => setDispatchOpen(true)}>
            Dispatch Excel
          </Button>
          )}
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/routes/add')}>
            Add Route
          </Button>
        </ListPageHeader>

        <ListFilterPanel
          hasActiveFilters={!!(search || fromHubId || toHubId || crossingHubId || viaHubId)}
          onClearFilters={clearFilters}
        >
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Service Number
            </label>
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Service number or name..."
              inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
          </div>
          {locationFilter('Source Location', fromHubId, setFromHubId)}
          {locationFilter('Target Location', toHubId, setToHubId)}
          {locationFilter('Middle Location (Via)', viaHubId, setViaHubId)}
          {locationFilter('Crossing Location', crossingHubId, setCrossingHubId)}
        </ListFilterPanel>

        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading routes..." />
          </div>
        ) : routes.length === 0 ? (
          <div className="p-8 text-center text-gray-600">No routes found</div>
        ) : (
          <>
            {/* Table - Desktop View */}
            <div className="hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0">
              <div className="w-full overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[720px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700 border-b">
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">SERVICE NO.</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">NAME</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold">PATH</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">TIME</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CROSSING</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {routes.map((route) => (
                      <tr key={route.route_id} className="hover:bg-gray-50 transition-colors relative">
                        <td className="px-4 py-3 whitespace-nowrap align-top">
                          <button
                            onClick={() => navigate(`/routes/view/${route.route_id}`)}
                            className={listIdLinkClass}
                          >
                            {route.route_id}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap align-top">
                          {route.name || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-sm align-top min-w-[160px] max-w-xs">
                          <RoutePathCell
                            path={formatPath(route)}
                            onViewFull={() => setViewPathRoute(route)}
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap align-top text-sm">
                          {route.departure_time || route.arrival_time
                            ? `${route.departure_time || '—'} – ${route.arrival_time || '—'}`
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 align-top min-w-[120px]">
                          <CrossingCell route={route} onOpen={() => setViewCrossingRoute(route)} />
                        </td>
                        <td className="px-4 py-3 relative whitespace-nowrap align-top">
                          <RowActionsMenu
                            isOpen={activeDropdown === route.route_id}
                            onToggle={() =>
                              setActiveDropdown(activeDropdown === route.route_id ? null : route.route_id)
                            }
                            onClose={() => setActiveDropdown(null)}
                            menuRef={dropdownRef}
                            onView={() => navigate(`/routes/view/${route.route_id}`)}
                            onEdit={() => navigate(`/routes/edit/${route.route_id}`)}
                            onDelete={() => handleDelete(route.route_id, route.name || route.route_id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 px-5 py-3 flex items-center justify-between border-t-2 border-gray-200">
                <span className="text-sm text-gray-600">
                  Showing{' '}
                  <span className="font-semibold">
                    {totalCount > 0 ? `${routes.length} of ${totalCount}` : routes.length}
                  </span>{' '}
                  route{totalCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="xl:hidden space-y-4">
              {routes.map((route) => (
                <div
                  key={route.route_id}
                  className="bg-white rounded-xl shadow p-4"
                >
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() => navigate(`/routes/view/${route.route_id}`)}
                        className={`${listIdLinkMobileClass} break-all text-left`}
                      >
                        {route.route_id}
                      </button>
                      <p className="text-sm text-gray-800 mt-1 font-medium break-words">
                        {route.name || '—'}
                      </p>
                    </div>
                    <RowActionsMenu
                      isOpen={activeDropdown === route.route_id}
                      onToggle={() =>
                        setActiveDropdown(activeDropdown === route.route_id ? null : route.route_id)
                      }
                      onClose={() => setActiveDropdown(null)}
                      menuRef={dropdownRef}
                      onView={() => navigate(`/routes/view/${route.route_id}`)}
                      onEdit={() => navigate(`/routes/edit/${route.route_id}`)}
                      onDelete={() => handleDelete(route.route_id, route.name || route.route_id)}
                    />
                  </div>
                  <div className="space-y-2 text-xs sm:text-sm border-t border-gray-100 pt-3">
                    <div>
                      <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                        Path
                      </p>
                      <RoutePathCell
                        path={formatPath(route)}
                        onViewFull={() => setViewPathRoute(route)}
                        singleLine
                      />
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-500">Time</span>
                      <span className="font-semibold text-gray-800">
                        {route.departure_time || route.arrival_time
                          ? `${route.departure_time || '—'} – ${route.arrival_time || '—'}`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 items-start">
                      <span className="text-gray-500 shrink-0">Crossing</span>
                      <CrossingCell route={route} onOpen={() => setViewCrossingRoute(route)} />
                    </div>
                  </div>
                </div>
              ))}

            </div>
            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading && currentPage > 1}
              onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
            />
          </>
        )}
      </div>

      {viewPathRoute && (
        <RouteTrackModal route={viewPathRoute} onClose={() => setViewPathRoute(null)} />
      )}
      {viewCrossingRoute && (
        <CrossingLocationsModal route={viewCrossingRoute} onClose={() => setViewCrossingRoute(null)} />
      )}
      <DispatchExcelModal
        open={dispatchOpen}
        onClose={() => setDispatchOpen(false)}
      />
    </DashboardLayout>
  );
};

export default RouteList;
